import { useState } from "react";
import { trpc } from "../hooks/trpc";
import { Download, Search, Filter, LogIn, Trash2, PenLine, Plus, Upload as UploadIcon, ShieldAlert, ShieldX, ShieldCheck, AlertTriangle } from "lucide-react";
import { Eyebrow } from "../components/brand";
import { AUTH_EVENT_ACTIONS } from "../../../shared/const";

const severityConfig: Record<string, { color: string; bg: string; label: string }> = {
  info: { color: "var(--color-info)", bg: "var(--color-info-bg)", label: "Info" },
  warning: { color: "var(--color-warning)", bg: "var(--color-warning-bg)", label: "Warnung" },
  error: { color: "var(--color-danger)", bg: "var(--color-danger-bg)", label: "Fehler" },
  critical: { color: "var(--color-danger)", bg: "var(--color-danger-bg)", label: "Kritisch" },
};

const actionIcons: Record<string, { icon: typeof LogIn; color: string; bg: string }> = {
  LOGIN: { icon: LogIn, color: "var(--color-success)", bg: "var(--color-success-bg)" },
  LOGOUT: { icon: LogIn, color: "var(--color-muted)", bg: "rgba(107,127,146,0.1)" },
  CREATE: { icon: Plus, color: "var(--color-primary)", bg: "rgba(0,163,224,0.1)" },
  UPDATE: { icon: PenLine, color: "var(--color-info)", bg: "var(--color-info-bg)" },
  DELETE: { icon: Trash2, color: "var(--color-danger)", bg: "var(--color-danger-bg)" },
  ADMIN_ACTION: { icon: Filter, color: "var(--color-warning)", bg: "var(--color-warning-bg)" },
  IMPORT: { icon: UploadIcon, color: "var(--color-primary)", bg: "rgba(0,163,224,0.1)" },
  LOGIN_FAILED: { icon: ShieldAlert, color: "var(--color-warning)", bg: "var(--color-warning-bg)" },
  LOGIN_BLOCKED: { icon: ShieldX, color: "var(--color-danger)", bg: "var(--color-danger-bg)" },
  BRUTE_FORCE_DETECTED: { icon: ShieldX, color: "var(--color-danger)", bg: "var(--color-danger-bg)" },
  SUSPICIOUS_REQUEST: { icon: ShieldX, color: "var(--color-danger)", bg: "var(--color-danger-bg)" },
  MFA_FAILED: { icon: ShieldAlert, color: "var(--color-warning)", bg: "var(--color-warning-bg)" },
  MFA_SUCCESS: { icon: ShieldCheck, color: "var(--color-success)", bg: "var(--color-success-bg)" },
  ACCESS_DENIED: { icon: ShieldAlert, color: "var(--color-warning)", bg: "var(--color-warning-bg)" },
  PASSWORD_CHANGED: { icon: PenLine, color: "var(--color-info)", bg: "var(--color-info-bg)" },
  SECURITY_SETTING_CHANGED: { icon: ShieldCheck, color: "var(--color-primary)", bg: "rgba(0,163,224,0.1)" },
  RATE_LIMIT_EXCEEDED: { icon: AlertTriangle, color: "var(--color-warning)", bg: "var(--color-warning-bg)" },
};

type FilterPreset = "all" | "auth" | "security" | "critical" | "failed_logins";

const presets: { key: FilterPreset; label: string }[] = [
  { key: "all", label: "Alle" },
  { key: "auth", label: "Anmeldungen" },
  { key: "security", label: "Sicherheitsvorfaelle" },
  { key: "critical", label: "Kritisch" },
  { key: "failed_logins", label: "Fehlgeschlagene Logins" },
];

export function AuditPage({ initialPreset = "all" }: { initialPreset?: FilterPreset } = {}) {
  const [filters, setFilters] = useState({ action: "", entityType: "", search: "", from: "", to: "", severity: "" });
  const [preset, setPreset] = useState<FilterPreset>(initialPreset);

  const applyPreset = (p: FilterPreset) => {
    setPreset(p);
    if (p === "all" || p === "auth") setFilters({ ...filters, severity: "", action: "" });
    else if (p === "security") setFilters({ ...filters, severity: "warning", action: "" });
    else if (p === "critical") setFilters({ ...filters, severity: "critical", action: "" });
    else if (p === "failed_logins") setFilters({ ...filters, severity: "", action: "LOGIN_FAILED" });
  };

  const query = trpc.audit.list.useQuery({
    limit: 100, offset: 0,
    action: filters.action || undefined,
    actions: preset === "auth" && !filters.action ? [...AUTH_EVENT_ACTIONS] : undefined,
    entityType: filters.entityType || undefined,
    search: filters.search || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
  });

  // Client-side severity filter (da tRPC-Endpoint severity-Filter noch nicht hat)
  const filteredData = query.data?.data.filter((entry: any) => {
    if (!filters.severity) return true;
    if (filters.severity === "warning") return ["warning", "error", "critical"].includes(entry.severity ?? "info");
    return (entry.severity ?? "info") === filters.severity;
  }) ?? [];

  const warnings24h = query.data?.data.filter((e: any) => {
    const ts = e.timestamp ? new Date(e.timestamp).getTime() : 0;
    const h24 = Date.now() - 24 * 60 * 60 * 1000;
    return ts > h24 && ["warning", "error"].includes(e.severity ?? "info");
  }).length ?? 0;

  const critical24h = query.data?.data.filter((e: any) => {
    const ts = e.timestamp ? new Date(e.timestamp).getTime() : 0;
    const h24 = Date.now() - 24 * 60 * 60 * 1000;
    return ts > h24 && (e.severity ?? "info") === "critical";
  }).length ?? 0;

  const exportQuery = trpc.audit.exportCsv.useQuery(
    { from: filters.from || undefined, to: filters.to || undefined },
    { enabled: false }
  );

  const handleExport = async () => {
    const result = await exportQuery.refetch();
    if (result.data?.csv) {
      const blob = new Blob([result.data.csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="animate-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
        <div>
          <Eyebrow>Audit · Live-Protokoll</Eyebrow>
          <h1
            className="text-2xl font-bold mt-4"
            style={{ fontFamily: "var(--font-display)", color: "var(--color-foreground)" }}
          >
            Audit-Log
          </h1>
          <p
            className="text-xs mt-2"
            style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)", letterSpacing: "0.1em", textTransform: "uppercase" }}
          >
            {query.data?.total ?? 0} Eintraege
            {warnings24h > 0 && (
              <> · <span style={{ color: "var(--color-warning)", fontWeight: 600 }}>{warnings24h} Warnungen</span></>
            )}
            {critical24h > 0 && (
              <> · <span style={{ color: "var(--color-danger)", fontWeight: 600 }}>{critical24h} kritisch</span></>
            )}
            <span style={{ color: "var(--color-muted)" }}> (24h)</span>
          </p>
        </div>
        <button onClick={handleExport} className="btn btn-secondary">
          <Download size={14} strokeWidth={1.5} /> CSV-Export
        </button>
      </div>

      {/* Presets */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {presets.map(p => (
          <button
            key={p.key}
            onClick={() => applyPreset(p.key)}
            className="px-3 py-1.5 text-xs font-medium transition-all cursor-pointer"
            style={{
              borderRadius: "var(--radius-sm)",
              background: preset === p.key ? "rgba(0,163,224,0.1)" : "transparent",
              color: preset === p.key ? "var(--color-primary)" : "var(--color-muted)",
              border: `1px solid ${preset === p.key ? "rgba(0,163,224,0.3)" : "var(--color-border)"}`,
              fontFamily: "var(--font-display)",
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 md:flex gap-3 mb-6">
        <div className="relative">
          <Search size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--color-muted)" }} />
          <input className="input pl-9 w-48" placeholder="Benutzer..." value={filters.search} onChange={e => { setFilters({ ...filters, search: e.target.value }); setPreset("all"); }} />
        </div>
        <select className="input w-44" value={filters.action} onChange={e => { setFilters({ ...filters, action: e.target.value }); setPreset("all"); }}>
          <option value="">Alle Aktionen</option>
          <option value="LOGIN">Login</option><option value="LOGOUT">Logout</option>
          <option value="LOGIN_FAILED">Login fehlgeschlagen</option>
          <option value="LOGIN_BLOCKED">Login blockiert</option>
          <option value="BRUTE_FORCE_DETECTED">Brute-Force</option>
          <option value="MFA_FAILED">MFA fehlgeschlagen</option>
          <option value="ACCESS_DENIED">Zugriff verweigert</option>
          <option value="SUSPICIOUS_REQUEST">Verdaechtig</option>
          <option value="CREATE">Erstellen</option><option value="UPDATE">Aendern</option>
          <option value="DELETE">Loeschen</option><option value="IMPORT">Import</option>
          <option value="PASSWORD_CHANGED">Passwort geaendert</option>
        </select>
        <select className="input w-36" value={filters.severity} onChange={e => { setFilters({ ...filters, severity: e.target.value }); setPreset("all"); }}>
          <option value="">Alle Severity</option>
          <option value="info">Info</option>
          <option value="warning">Warnung+</option>
          <option value="critical">Kritisch</option>
        </select>
        <input type="date" className="input w-36" value={filters.from} onChange={e => setFilters({ ...filters, from: e.target.value })} />
        <input type="date" className="input w-36" value={filters.to} onChange={e => setFilters({ ...filters, to: e.target.value })} />
      </div>

      {/* Table */}
      <div className="card overflow-x-auto">
        <table className="table">
          <thead><tr><th>Zeitpunkt</th><th>Severity</th><th>Benutzer</th><th>Aktion</th><th>Entitaet</th><th>Details</th><th>IP</th></tr></thead>
          <tbody>
            {filteredData.map((entry: any) => {
              const cfg = actionIcons[entry.action] ?? actionIcons.CREATE;
              const Icon = cfg.icon;
              const sev = severityConfig[entry.severity ?? "info"] ?? severityConfig.info;
              const isCritical = entry.severity === "critical";
              return (
                <tr key={entry.id} style={isCritical ? { borderLeft: "3px solid var(--color-danger)" } : undefined}>
                  <td
                    className="text-xs whitespace-nowrap"
                    style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}
                  >
                    {entry.timestamp ? new Date(entry.timestamp).toLocaleString("de") : "—"}
                  </td>
                  <td>
                    <span className="badge" style={{ background: sev.bg, color: sev.color }}>{sev.label}</span>
                  </td>
                  <td className="font-medium">{entry.username ?? "—"}</td>
                  <td>
                    <span className="badge" style={{ background: cfg.bg, color: cfg.color }}>
                      <Icon size={11} strokeWidth={1.5} /> {entry.action}
                    </span>
                  </td>
                  <td style={{ color: "var(--color-muted)" }}>{entry.entityType ?? "—"}</td>
                  <td
                    className="text-xs max-w-[200px] truncate"
                    style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}
                  >
                    {entry.details ? JSON.stringify(entry.details) : "—"}
                  </td>
                  <td className="text-xs" style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}>
                    {entry.ip ?? "—"}
                  </td>
                </tr>
              );
            })}
            {filteredData.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-8" style={{ color: "var(--color-muted)" }}>
                  Keine Log-Eintraege gefunden.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
